import React from 'react';

import { useSelector } from 'react-redux';

import Avatar from '@material-ui/core/Avatar';

import { userSelector } from '../../reducers/current-user-selectors';

import { InlineContactFile } from '../legacy-components/inline-contact-file';

import { MessageDeliverability } from './message-deliverability';
import { TranslateMessageButton } from './translate-message-button';

import { MessageType } from '../../util/message-types';
import { FilesDisplay } from '../../widgets/message-composer/toolbar/files-display';

type InputProps = {
  isAnnouncementThread: boolean;
  announcement?: Announcement | null;
  announcementDeliverySummary?: AnnouncementDeliverySummary | null;
  messageSid?: string;
  messageType?: MessageType;
  isFailed?: boolean;
  isInbound?: boolean;
  isScheduledToSend?: boolean;
  messageIdAndLanguageCode?: MessageIdAndLanguageCodeType;

  disableTranslateButton: boolean;
  disableTranslateButtonMessage: string | null;
  shouldShowTranslation: boolean;
  translationLoading: boolean;
  imageUrls?: string;
  contactFilesWithUrls?: { contactFile: ContactFile; presignedUrl: string }[];
  requiresResponse?: boolean | null;
  urlMappingsForLanguage?: UrlFileMapping[];
  onTranslateMessageClick: () => void;
  hideTranslation: () => void;
  showDidTruncateMessage?: boolean;
  didTruncateMessage?: boolean;
  showFullMessage?: boolean;
  handleShowFullMessageClick: () => void;
  isPreview?: boolean;
};

export const MessageItemFooter = (props: InputProps) => {
  const {
    announcement,
    announcementDeliverySummary,
    messageSid,
    messageType,
    isFailed,
    isInbound,
    isScheduledToSend,
    messageIdAndLanguageCode,
    shouldShowTranslation,
    translationLoading,
    imageUrls,
    contactFilesWithUrls,
    requiresResponse,
    urlMappingsForLanguage,
    disableTranslateButton,
    disableTranslateButtonMessage,
    onTranslateMessageClick,
    hideTranslation,
    showDidTruncateMessage,
    didTruncateMessage,
    showFullMessage,
    handleShowFullMessageClick,
    isAnnouncementThread,
    isPreview,
  } = props;

  const user = useSelector(userSelector);

  return (
    <div className="u-padding-top u-display-flex u-justifyContent-spaceBetween">
      <div className="u-display-flex u-flexDirection-column u-justifyContent-flexEnd typo-subtitle">
        <FilesDisplay urlFileMappings={urlMappingsForLanguage} isPreview />
        {imageUrls &&
          imageUrls.split(',').map((url) => (
            <a href={url} target="_blank" rel="noopener noreferrer">
              <img
                alt="sent in text message"
                key={url}
                src={url}
                className="u-maxWidth-400px u-margin-0Auto"
              />
            </a>
          ))}
        {contactFilesWithUrls?.map(({ contactFile, presignedUrl }) => (
          <InlineContactFile
            key={`contact-file-${contactFile.contactFileId}`}
            contactFile={contactFile}
            presignedUrl={presignedUrl}
          />
        ))}

        {user.isAdmin && requiresResponse && !isPreview && (
          <Avatar
            variant="circle"
            className="u-position-absolute u-margin-top u-margin-right u-width-25px u-height-25px typo-title"
          >
            R
          </Avatar>
        )}
        {showDidTruncateMessage ? (
          <div className={didTruncateMessage ? 'u-spread' : ''}>
            {didTruncateMessage ? (
              <div className="typo-link" role="presentation" onClick={handleShowFullMessageClick}>
                {showFullMessage ? 'Show Less...' : 'Show More...'}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {isPreview ? null : (
        <div className="u-display-flex u-justifyContent-flexEnd u-alignItems-flexEnd typo-subtitle">
          {messageIdAndLanguageCode?.messageId && (
            <TranslateMessageButton
              isTranslated={shouldShowTranslation}
              languageCode={messageIdAndLanguageCode.messageLanguageCode}
              loading={translationLoading}
              onTranslateMessageClick={onTranslateMessageClick}
              onShowOriginalClick={hideTranslation}
              disabled={disableTranslateButton}
              disabledTooltip={disableTranslateButtonMessage}
            />
          )}
          {!isInbound && (
            <div className="u-margin-left">
              <MessageDeliverability
                announcement={announcement}
                announcementDeliverySummary={announcementDeliverySummary}
                messageSid={messageSid}
                isFailed={isFailed}
                isScheduledToSend={isScheduledToSend}
                messageType={messageType}
                isAnnouncementThread={isAnnouncementThread}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
